// packages/ui-components/src/components/surface/MeasuredContainer.tsx
import { useRef, useState, useEffect, type CSSProperties, type ReactNode } from 'react';

interface MeasuredContainerProps {
  className?: string;
  style?: CSSProperties;
  children: (width: number, height: number) => ReactNode;
}

/**
 * Wraps stretch components with a continuously-measured container.
 * Passes concrete pixel dimensions to children via render prop.
 *
 * Used by NodeRenderer to give stretch components (charts, tables, etc.)
 * their allocated size. Children are deferred until the first valid
 * measurement so layout is stable.
 *
 * This replaces SizedContainer (which measured once and locked) and
 * eliminates the need for Recharts' ResponsiveContainer (which added
 * a redundant second ResizeObserver).
 */
export function MeasuredContainer({ className, style, children }: MeasuredContainerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const sizeRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Synchronous initial measurement — if layout is already computed
    // (common on client-side nav), render children on the first frame.
    const w = Math.round(el.clientWidth);
    const h = Math.round(el.clientHeight);
    if (w > 0 && h > 0) {
      sizeRef.current = { width: w, height: h };
      setSize({ width: w, height: h });
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        const rw = Math.round(width);
        const rh = Math.round(height);
        const prev = sizeRef.current;
        if (prev && prev.width === rw && prev.height === rh) return;
        const next = { width: rw, height: rh };
        sizeRef.current = next;
        setSize(next);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className} style={style}>
      {size ? children(size.width, size.height) : null}
    </div>
  );
}
